/**
 * Delivery-schedule math — CLIENT MIRROR of
 * netlify/functions/_lib/delivery-schedule.ts (the server source of truth).
 * Keep the two in sync (same pairing as willCall.ts / will-call.ts).
 *
 * All clock math is America/Los_Angeles; date strings are YYYY-MM-DD and
 * day-of-week is derived UTC-anchored (never new Date('YYYY-MM-DD') local).
 */

export interface DeliverySchedule {
  enabled: boolean;
  /** Day indices, 0=Sunday … 6=Saturday. Stored in jsonb as lowercase names. */
  days: number[];
  /** 1 = weekly, 2 = every two weeks. */
  every_n_weeks: number;
  /** Biweekly phase anchor (a date that IS a delivery day). Null when weekly. */
  anchor_date: string | null;
  /** 'HH:MM' 24h, Pacific — the order cutoff on the day BEFORE delivery. */
  cutoff_time: string;
  reminder_enabled: boolean;
  reminder_emails: string[];
  /** Stamped when saved — the reminder never fires for dates before this. */
  enabled_at: string | null;
}

export const DAY_NAMES = [
  'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday',
] as const;

const DAY_LABELS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const DEFAULT_CUTOFF_TIME = '16:00';

/** Parse the delivery_schedule jsonb defensively. Null on absent/junk. */
export function parseDeliverySchedule(raw: unknown): DeliverySchedule | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const days = (Array.isArray(r.days) ? r.days : [])
    .map((d) => {
      if (typeof d === 'number' && Number.isInteger(d) && d >= 0 && d <= 6) return d;
      const idx = DAY_NAMES.indexOf(String(d).trim().toLowerCase() as (typeof DAY_NAMES)[number]);
      return idx;
    })
    .filter((d) => d >= 0);
  const uniqueDays = [...new Set(days)].sort((a, b) => a - b);
  if (uniqueDays.length === 0) return null;

  const n = Number(r.every_n_weeks ?? 1);
  const cutoff = String(r.cutoff_time ?? DEFAULT_CUTOFF_TIME);
  const anchor = String(r.anchor_date ?? '').trim();
  return {
    enabled: r.enabled === true,
    days: uniqueDays,
    every_n_weeks: Number.isInteger(n) && n >= 1 && n <= 4 ? n : 1,
    anchor_date: /^\d{4}-\d{2}-\d{2}$/.test(anchor) ? anchor : null,
    cutoff_time: /^\d{2}:\d{2}$/.test(cutoff) ? cutoff : DEFAULT_CUTOFF_TIME,
    reminder_enabled: r.reminder_enabled !== false,
    reminder_emails: (Array.isArray(r.reminder_emails) ? r.reminder_emails : [])
      .map((e) => String(e).trim().toLowerCase())
      .filter((e) => e.includes('@')),
    enabled_at: typeof r.enabled_at === 'string' && r.enabled_at ? r.enabled_at : null,
  };
}

/** Effective schedule for a location: its own override wins, else the
 *  customer default. Only ENABLED schedules count. */
export function effectiveSchedule(
  locationRaw: unknown,
  customerRaw: unknown,
): DeliverySchedule | null {
  const loc = parseDeliverySchedule(locationRaw);
  if (loc) return loc.enabled ? loc : null; // an explicit disabled override turns it OFF
  const cust = parseDeliverySchedule(customerRaw);
  return cust && cust.enabled ? cust : null;
}

/* -------------------- date primitives (UTC-anchored) -------------------- */

export function addDays(dateStr: string, days: number): string {
  const d = new Date(`${dateStr}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function dayOfWeek(dateStr: string): number {
  return new Date(`${dateStr}T12:00:00Z`).getUTCDay();
}

function diffDays(a: string, b: string): number {
  const ms = new Date(`${a}T12:00:00Z`).getTime() - new Date(`${b}T12:00:00Z`).getTime();
  return Math.round(ms / 86_400_000);
}

/** The Monday of the calendar week containing dateStr. */
function mondayOf(dateStr: string): string {
  const dow = dayOfWeek(dateStr);
  return addDays(dateStr, dow === 0 ? -6 : 1 - dow);
}

/** Current date + clock in America/Los_Angeles. */
export function ptNow(now: Date = new Date()): { date: string; hour: number; minute: number } {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(now);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return {
    date: `${get('year')}-${get('month')}-${get('day')}`,
    // en-CA can emit hour '24' at midnight — normalize.
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
  };
}

/* -------------------- schedule queries -------------------- */

/** Is dateStr a delivery day under this schedule (day-of-week + biweekly
 *  calendar-week cycle)? Ignores the cutoff — see isOrderable. */
export function isDeliveryDay(s: DeliverySchedule, dateStr: string): boolean {
  if (!s.enabled || !s.days.includes(dayOfWeek(dateStr))) return false;
  if (s.every_n_weeks <= 1) return true;
  if (!s.anchor_date) return true; // no anchor → degrade to weekly rather than never
  const weeks = Math.round(diffDays(mondayOf(dateStr), mondayOf(s.anchor_date)) / 7);
  const n = s.every_n_weeks;
  return ((weeks % n) + n) % n === 0;
}

/** The order cutoff instant for a delivery date: cutoff_time PT on the
 *  PREVIOUS day. */
export function cutoffFor(
  s: DeliverySchedule,
  deliveryDate: string,
): { date: string; hour: number; minute: number } {
  const [h, m] = s.cutoff_time.split(':').map(Number);
  return { date: addDays(deliveryDate, -1), hour: h ?? 16, minute: m ?? 0 };
}

/** Can an order still be placed for this delivery date right now? */
export function isOrderable(s: DeliverySchedule, deliveryDate: string, now?: Date): boolean {
  const pt = ptNow(now);
  if (deliveryDate <= pt.date) return false; // same-day/past: cutoff already gone
  const cut = cutoffFor(s, deliveryDate);
  if (cut.date > pt.date) return true;
  if (cut.date < pt.date) return false;
  return pt.hour * 60 + pt.minute < cut.hour * 60 + cut.minute;
}

const SCAN_DAYS = 35; // covers every biweekly phase with headroom

/** The next delivery day whose cutoff hasn't passed — the order default. */
export function nextOrderableDeliveryDate(s: DeliverySchedule, now?: Date): string | null {
  const today = ptNow(now).date;
  for (let i = 1; i <= SCAN_DAYS; i++) {
    const d = addDays(today, i);
    if (isDeliveryDay(s, d) && isOrderable(s, d, now)) return d;
  }
  return null;
}

/** First orderable delivery day ON or AFTER dateStr (EDI PO due-date snap) —
 *  i.e. the next route day after "now", with dateStr as a "not before" floor.
 *  A dateStr so stale the scan window ends in the past (a re-forwarded old
 *  PO) degrades to the next orderable day, same as a junk date. Null only
 *  when the schedule itself yields no orderable day at all. */
export function snapToSchedule(s: DeliverySchedule, dateStr: string, now?: Date): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return nextOrderableDeliveryDate(s, now);
  for (let i = 0; i <= SCAN_DAYS; i++) {
    const d = addDays(dateStr, i);
    if (isDeliveryDay(s, d) && isOrderable(s, d, now)) return d;
  }
  return nextOrderableDeliveryDate(s, now);
}

/** The delivery date whose CUTOFF DAY is today (for the reminder cron):
 *  cutoff is always the day before, so this is simply tomorrow when tomorrow
 *  is a delivery day. */
export function deliveryDateWithCutoffToday(s: DeliverySchedule, now?: Date): string | null {
  const tomorrow = addDays(ptNow(now).date, 1);
  return isDeliveryDay(s, tomorrow) ? tomorrow : null;
}

/* -------------------- display helpers -------------------- */

/** "Tuesdays & Fridays" / "Every 2 weeks: Mondays". */
export function describeSchedule(s: DeliverySchedule): string {
  const names = s.days.map((d) => `${DAY_LABELS[d]}s`);
  const list =
    names.length <= 1
      ? (names[0] ?? '')
      : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
  return s.every_n_weeks > 1 ? `Every ${s.every_n_weeks} weeks: ${list}` : list;
}

/** "4:00 PM" from '16:00'. */
export function fmtCutoffTime(s: DeliverySchedule): string {
  const [h, m] = s.cutoff_time.split(':').map(Number);
  const hour = ((h ?? 16) + 11) % 12 + 1;
  const ampm = (h ?? 16) >= 12 ? 'PM' : 'AM';
  return `${hour}:${String(m ?? 0).padStart(2, '0')} ${ampm}`;
}

/** "Friday, Aug 21" — PT-safe for date-only strings. */
export function fmtScheduleDate(dateStr: string): string {
  return new Date(`${dateStr}T12:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}
